import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@src/common/components/dates/DatePicker';
import { Stack } from '@src/common/components/Stack';
import { css } from '@src/common/styles/Styles';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props<T extends FieldValues> {
  control: Control<T>;
  fieldName: Path<T>;
  label?: string;
  readOnly?: boolean;
  disabled?: boolean;
  isUTC?: boolean;
  endAdornment?: JSX.Element;
}

dayjs.extend(utc);

export const FormDateTimePicker = <T extends FieldValues>({
  control,
  fieldName,
  label,
  readOnly,
  disabled = false,
  isUTC,
  endAdornment,
}: Props<T>) => {
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({
        field,
        fieldState: { error },
        formState: { disabled: formDisabled },
      }) => {
        const onDateChange = (newDate: Date | null) => {
          field.onChange(newDate);
        };

        const onTimeChange = (newDate: Dayjs | null) => {
          if (newDate && !newDate.isValid()) {
            return;
          }
          field.onChange(newDate?.toDate());
        };

        return (
          <Stack direction="row">
            <DatePicker
              {...field}
              readOnly={readOnly || formDisabled || disabled}
              onChange={onDateChange}
              value={field.value || null}
              className={inputs}
              label={label ?? t(`fields.${fieldName}`)}
              error={error && t(`formErrors.${error!.message}`)}
              sx={{
                '& fieldset': {
                  borderRight: 'none',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
              isUTC={isUTC}
              endAdornment={endAdornment}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                {...field}
                readOnly={readOnly || formDisabled || disabled}
                onChange={onTimeChange}
                value={field.value ? dayjs(field.value) : null}
                className={inputs}
                format="hh:mm"
                sx={{
                  '& fieldset': {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                }}
                slotProps={{
                  textField: {
                    error: !!error,
                  },
                }}
                timezone="UTC"
              />
            </LocalizationProvider>
          </Stack>
        );
      }}
    />
  );
};

const inputs = css({
  flex: 1,
});
